import config from "../../config";
import { BRANDS, PRODUCT_URLS, type ServiceKey } from "./pairGuideBrands";
import { ServicePairFlowVisual } from "./visuals/ServicePairFlowVisual";

type Props = {
    sourceKey: ServiceKey;
    destKey: ServiceKey;
};

export function PairTransferGuideContent({ sourceKey, destKey }: Props) {
    const source = BRANDS[sourceKey];
    const dest = BRANDS[destKey];
    const destUrl = PRODUCT_URLS[destKey];
    const same = sourceKey === destKey;
    const stepAccent = source.spanClass;

    const tipsTitle = same
        ? `Pro tips for ${source.name} transfer`
        : `Pro tips for ${source.name} to ${dest.name} transfer`;

    const tip1 =
        destKey === "appleMusic"
            ? {
                  title: "Check audio quality",
                  body: "Apple Music offers lossless audio. Consider a plan that supports higher quality after your library lands.",
              }
            : {
                  title: "Check your transfer report",
                  body: "Review skipped or unmatched tracks in Music Porter after each run so nothing important is missing.",
              };

    const tip2 = {
        title: "Verify matches when available",
        body: 'Use verify options in Music Porter when you want to double-check matches before finishing a transfer.',
    };

    const tip3 = {
        title: "Keep accounts connected",
        body: "Leave both services signed in until the job completes so OAuth tokens stay valid.",
    };

    return (
        <div className="bg-white dark:bg-zinc-950 text-gray-900 dark:text-white">
            <div className="max-w-screen-xl pt-20 px-4 py-8 mx-auto lg:py-24 lg:px-6 lg:pt-28">
                <section className="bg-white dark:bg-zinc-950">
                    <div className="max-w-screen-md mx-auto mb-8 text-center lg:mb-12">
                        <h1 className="mb-4 text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white lg:text-5xl">
                            Transfer from <span className={source.spanClass}>{source.name}</span> to{" "}
                            <span className={dest.spanClass}>{dest.name}</span>
                        </h1>
                        <p className="mb-8 font-light text-gray-500 sm:text-xl dark:text-gray-400">
                            {same
                                ? `Copy playlists, albums, and saved tracks between ${source.name} accounts in a few guided steps.`
                                : `Seamlessly transfer your curated playlists, albums, and liked songs from ${source.name} to ${dest.name} in just a few guided steps.`}
                        </p>
                        <p className="text-lg text-gray-600 dark:text-gray-300">
                            Open {dest.name}:{" "}
                            <a
                                href={destUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-purple-600 dark:text-purple-400 underline font-medium hover:text-purple-700 dark:hover:text-purple-300"
                            >
                                {dest.name}
                            </a>
                        </p>
                        <ServicePairFlowVisual sourceKey={sourceKey} destKey={destKey} mode="transfer" />
                    </div>
                </section>

                <section className="bg-gray-50 dark:bg-zinc-900">
                    <div className="max-w-screen-xl px-4 py-16 mx-auto lg:py-24 lg:px-6">
                        <div className="max-w-screen-md mx-auto text-center mb-12">
                            <h2 className="mb-4 text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white">
                                {same
                                    ? `Move your library on ${source.name}`
                                    : `Move your music from ${source.name} to ${dest.name}`}
                            </h2>
                            <p className="text-gray-500 dark:text-gray-400">
                                Whether you are switching platforms or want your music on both services,
                                <span className="font-semibold text-purple-600 dark:text-purple-400"> Music Porter </span>
                                makes it seamless. Transfer playlists, albums, liked tracks, and artists in just a few clicks.
                            </p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            <div className="text-center">
                                <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white font-bold text-xl">
                                    1
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Connect accounts</h3>
                                <p className="text-gray-500 dark:text-gray-400">
                                    Link your {source.name} and {dest.name} accounts in Music Porter.
                                </p>
                            </div>
                            <div className="text-center">
                                <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white font-bold text-xl">
                                    2
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Select content</h3>
                                <p className="text-gray-500 dark:text-gray-400">
                                    Choose {source.name} as the source and pick playlists, albums, and tracks to copy to {dest.name}.
                                </p>
                            </div>
                            <div className="text-center">
                                <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white font-bold text-xl">
                                    3
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Transfer and enjoy</h3>
                                <p className="text-gray-500 dark:text-gray-400">
                                    Start the transfer and open your library on {dest.name} when the job completes.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="bg-white dark:bg-zinc-950 text-gray-900 dark:text-white mb-10 mt-10">
                    <div className="max-w-screen-xl px-4 py-8 mx-auto lg:py-16 lg:px-6">
                        <div className="max-w-screen-sm mx-auto text-center">
                            <h2 className="mb-4 text-3xl font-extrabold leading-tight tracking-tight text-gray-900 dark:text-white">
                                Ready to transfer from {source.name} to {dest.name}?
                            </h2>
                            <p className="mb-6 font-light text-gray-500 dark:text-gray-400 md:text-lg">
                                Start your first transfer for free today.
                            </p>
                            <div className="space-y-4 sm:flex sm:space-y-0 sm:space-x-4 justify-center">
                                <a
                                    href={config.signupPath}
                                    className="inline-flex items-center justify-center w-full px-5 py-3 text-sm font-medium text-center text-white bg-purple-700 border border-transparent rounded-lg sm:w-auto hover:bg-purple-800 focus:ring-4 focus:ring-purple-300 dark:bg-purple-600 dark:hover:bg-purple-700 dark:focus:ring-purple-800"
                                >
                                    Sign up for free
                                </a>
                                <a
                                    href="/transfer-playlists-and-songs"
                                    className="text-purple-700 bg-white border border-purple-700 hover:bg-purple-50 focus:ring-4 focus:ring-purple-300 font-medium rounded-lg text-sm px-8 py-3 dark:text-purple-400 dark:border-purple-400 dark:hover:bg-purple-900/20 focus:outline-none dark:focus:ring-purple-800 transition-colors duration-200"
                                >
                                    Learn About Transfer
                                </a>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="bg-white dark:bg-zinc-950 py-12">
                    <div className="max-w-screen-xl px-4 mx-auto lg:px-6">
                        <div className="max-w-screen-md mx-auto text-center mb-12">
                            <h2 className="mb-4 text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white">
                                How to transfer from {source.name} to {dest.name}
                            </h2>
                            <p className="text-gray-500 dark:text-gray-400">Follow these simple steps to move your music library.</p>
                        </div>
                        <div className="max-w-4xl mx-auto">
                            <div className="bg-white dark:bg-zinc-950 rounded-2xl border border-zinc-200 dark:border-zinc-800 shadow-sm p-8">
                                <div className="space-y-8">
                                    <div className="flex items-start gap-4">
                                        <div
                                            className={`text-2xl font-bold ${stepAccent} bg-gray-100 dark:bg-zinc-800 rounded-full w-12 h-12 flex items-center justify-center`}
                                        >
                                            1
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Log in to Music Porter</h3>
                                            <p className="text-lg text-gray-800 dark:text-gray-300 leading-relaxed">
                                                Sign in to{" "}
                                                <span className="font-semibold text-purple-700 dark:text-purple-400">Music Porter</span> and connect
                                                your {source.name} and {dest.name} accounts using the <b>Add New Account</b> button.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-4">
                                        <div
                                            className={`text-2xl font-bold ${stepAccent} bg-gray-100 dark:bg-zinc-800 rounded-full w-12 h-12 flex items-center justify-center`}
                                        >
                                            2
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Select {source.name} content</h3>
                                            <p className="text-lg text-gray-800 dark:text-gray-300 leading-relaxed">
                                                Choose {source.name} as the source and browse playlists, albums, and liked songs.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-4">
                                        <div
                                            className={`text-2xl font-bold ${stepAccent} bg-gray-100 dark:bg-zinc-800 rounded-full w-12 h-12 flex items-center justify-center`}
                                        >
                                            3
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Choose what to transfer</h3>
                                            <p className="text-lg text-gray-800 dark:text-gray-300 leading-relaxed">
                                                Select what you want copied to {dest.name}, then review your selection.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-4">
                                        <div
                                            className={`text-2xl font-bold ${stepAccent} bg-gray-100 dark:bg-zinc-800 rounded-full w-12 h-12 flex items-center justify-center`}
                                        >
                                            4
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Start transfer</h3>
                                            <p className="text-lg text-gray-800 dark:text-gray-300 leading-relaxed">
                                                Click <span className="font-semibold">Start Transfer</span> and pick {dest.name} as the destination.
                                                Use any verify options Music Porter offers to confirm matches before finishing.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="bg-white dark:bg-zinc-950 py-12">
                    <div className="max-w-screen-xl px-4 mx-auto lg:px-6">
                        <div className="max-w-screen-md mx-auto text-center mb-12">
                            <h2 className="mb-4 text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white">{tipsTitle}</h2>
                            <p className="text-gray-500 dark:text-gray-400">Make the most of your transfer with these tips.</p>
                        </div>
                        <div className="grid md:grid-cols-3 gap-8">
                            {[tip1, tip2, tip3].map((tip, i) => (
                                <div
                                    key={i}
                                    className="bg-zinc-100 dark:bg-zinc-800 p-6 rounded-2xl shadow hover:shadow-lg transition-all duration-300"
                                >
                                    <div className="text-4xl font-extrabold text-purple-600 dark:text-purple-400 mb-4">
                                        {String(i + 1).padStart(2, "0")}
                                    </div>
                                    <h3 className="text-xl font-semibold mb-2">{tip.title}</h3>
                                    <p className="text-base text-gray-700 dark:text-gray-300">{tip.body}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            </div>

            <section className="bg-gray-50 dark:bg-zinc-800">
                <div className="max-w-screen-xl px-4 py-16 mx-auto lg:py-24 lg:px-6">
                    <div className="max-w-screen-md mx-auto text-center">
                        <h2 className="mb-6 text-3xl font-extrabold leading-tight tracking-tight text-gray-900 dark:text-white lg:text-4xl">
                            Ready to move your {source.name} playlists to {dest.name}?
                        </h2>
                        <p className="mb-8 font-light text-gray-500 dark:text-gray-400 md:text-lg">
                            Join thousands of listeners who use Music Porter to move libraries between streaming services.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                            <a
                                href={config.signupPath}
                                className="text-white bg-purple-700 hover:bg-purple-800 focus:ring-4 focus:ring-purple-300 font-medium rounded-lg text-sm px-8 py-3 dark:bg-purple-600 dark:hover:bg-purple-700 focus:outline-none dark:focus:ring-purple-800 transition-colors duration-200"
                            >
                                Start Now
                            </a>
                            <a
                                href="/sync-playlists"
                                className="text-purple-700 bg-white border border-purple-700 hover:bg-purple-50 focus:ring-4 focus:ring-purple-300 font-medium rounded-lg text-sm px-8 py-3 dark:text-purple-400 dark:border-purple-400 dark:hover:bg-purple-900/20 focus:outline-none dark:focus:ring-purple-800 transition-colors duration-200"
                            >
                                Learn About Sync
                            </a>
                        </div>
                        <div className="mt-8 text-sm text-gray-500 dark:text-gray-400">
                            <p>✓ No credit card required • ✓ Free forever transfer • ✓ Lifetime plan available</p>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
